import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { liveChallengesApi } from '@/services/liveChallenges';
import { PATHS } from '@/routes/paths';

const STATUS_CONFIG = {
  lobby: { label: 'Lobby', variant: 'warning' },
  in_progress: { label: 'Em Andamento', variant: 'primary-light' },
  finished: { label: 'Finalizada', variant: 'success-light' }
};

const normalizeJsonApi = items =>
  items.map(item => ({
    id: item.id,
    ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
  }));

export default function ActivationsList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activations, setActivations] = useState([]);
  const [challengeTitle, setChallengeTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivations = async () => {
      setLoading(true);
      // Fetches paralelos com falha independente: título indisponível não pode
      // derrubar a lista nem disparar toast enganoso (e vice-versa).
      const [activationsResult, challengeResult] = await Promise.allSettled([
        liveChallengesApi.activations(id),
        // RECONCILIAÇÃO backend: index de ativações não envia meta — título vem do show do template.
        liveChallengesApi.show(id)
      ]);
      if (activationsResult.status === 'fulfilled') {
        setActivations(normalizeJsonApi(activationsResult.value?.data ?? []));
      } else {
        toast.error('Erro ao carregar ativações.');
      }
      if (challengeResult.status === 'fulfilled') {
        setChallengeTitle(challengeResult.value?.data?.attributes?.title ?? '');
      }
      setLoading(false);
    };
    fetchActivations();
  }, [id]);

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      { route: PATHS.LIVE_CHALLENGE.ADMIN_LIST, name: 'Desafio ao Vivo' },
      { route: '#', name: challengeTitle ? `Ativações — ${challengeTitle}` : 'Ativações', isActive: true }
    ]
  };

  const formatDateTime = value => (value ? new Date(value).toLocaleString('pt-BR') : '—');

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loading ? (
        <Loader />
      ) : activations.length === 0 ? (
        <EmptyState
          type="data"
          text="Este desafio ainda não foi ativado."
        />
      ) : (
        <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
          {activations.map(activation => {
            const config = STATUS_CONFIG[activation.status] || STATUS_CONFIG.lobby;
            return (
              <li
                key={activation.id}
                className="flex flex-col gap-3 border-b border-neutral-lightest px-5 py-3 md:flex-row md:items-center md:gap-6"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="m-0 text-sm font-semibold text-neutral-dark">{formatDateTime(activation.started_at || activation.created_at)}</h3>
                    <Badge
                      variant={config.variant}
                      size="xs"
                    >
                      {config.label}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral">
                    Host: {activation.host?.name || '—'} &middot; {activation.participants_count ?? 0} participantes &middot; Código: {activation.access_code || '—'}
                  </p>
                  {Array.isArray(activation.top_participants) && activation.top_participants.length > 0 && (
                    <p className="mt-0.5 text-xs text-neutral-light">
                      Top 3:{' '}
                      {activation.top_participants
                        .slice(0, 3)
                        .map((p, i) => `${i + 1}º ${p.name} (${p.total_score})`)
                        .join(' · ')}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant={activation.status === 'finished' ? 'success' : 'default'}
                    size="xs"
                    onClick={() => navigate(PATHS.LIVE_CHALLENGE.ADMIN_PANEL(activation.id))}
                  >
                    {activation.status === 'finished' ? 'Ver resultado' : 'Abrir painel'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardContent>
  );
}
